// Map Markers Module - manages markers on the map
import { getToken } from './map.token.js';

// Private marker references
const markers = {
    search: null
};

// Helper function to wait for a map event once
function once(map, eventName) {
    return new Promise(resolve => {
        const handler = () => {
            map.events.remove(eventName, handler);
            resolve();
        };
        map.events.add(eventName, handler);
    });
}

// Helper function to wait for rendered shapes at a position
// County parcel layer, then muni layers (required) before invoking click
async function waitForRenderedHit(map, lngLat, tries = 20, delayMs = 75) {

    const muniLayerIds = [
        'towb-zoning-fill',
        'tocb-zoning-fill',
        'wilmington-zoning-fill',
        'nhc-zoning-fill',
    ];

    var countyLoaded = false;
    var muniLoaded = false;

    let renderedShapes = [];
    for (let i = 0; i < tries; i++) {
        const shapes = map.layers.getRenderedShapes(lngLat, ['nhc_parcels-zoning-fill']);
        if (shapes && shapes.length > 0) {
            countyLoaded = true;
            renderedShapes.push(shapes);
            break;
        };
        await new Promise(r => setTimeout(r, delayMs));
    }

    if (countyLoaded) {
        for (let i = 0; i < tries; i++) {
            for (var layerId of muniLayerIds) {
                const shapes = map.layers.getRenderedShapes(lngLat, [layerId]);
                if (shapes && shapes.length > 0) {
                    muniLoaded = true;
                    renderedShapes.push(shapes);
                    break;
                };
            }
            if (muniLoaded) break;

            await new Promise(r => setTimeout(r, delayMs));
        }
    }

    if (!(countyLoaded && muniLoaded)) {
        return null;
    }

    // Overlay layers (1945, historic, height) use separate tile sources; wait until they have
    // shapes at this position (or max tries) so we don't invoke click before they're loaded
    const overlayLayerIds = [
        'wilmington-1945-zoning-fill',
        'wilmington-historic-zoning-fill',
        'tocb-heightoverlay-zoning-fill',
    ];
    const overlayTries = 10; // ~750ms max; proceed when we get overlay shapes or timeout
    for (let i = 0; i < overlayTries; i++) {
        let overlayLoaded = false;
        for (var layerId of overlayLayerIds) {
            const shapes = map.layers.getRenderedShapes(lngLat, [layerId]);
            if (shapes && shapes.length > 0) {
                overlayLoaded = true;
                renderedShapes.push(shapes);
            }
        }
        if (overlayLoaded) break;
        await new Promise(r => setTimeout(r, delayMs));
    }

    return renderedShapes;
}

// Shared function to navigate to position, wait for layer to render, and trigger click
async function navigateAndClick(map, center, maxZoom) {
    const moveEndPromise = once(map, "moveend");
    
    map.setCamera({ center, zoom: maxZoom, type: "ease", duration: 600 });
    
    await moveEndPromise;
    
    // give it a frame to render tiles
    await new Promise(r => requestAnimationFrame(() => requestAnimationFrame(r)));
    
    const hit = await waitForRenderedHit(map, center);
    if (!hit) return;
    
    // Simulate a click where the parcel actually rendered
    map.events.invoke("click", {
        position: center,
        location: center
    });
}

// Public API
const MapMarkers = {
    addSearchMarker: function (map, position, address) {
        // Remove existing search marker if it exists
        if (markers.search) {
            map.markers.remove(markers.search);
        }

        // Add a new marker at the geocoded location
        markers.search = new atlas.HtmlMarker({
            position: position,
            color: 'DodgerBlue',
            text: '📍',
            popup: new atlas.Popup({
                content: `<div style="padding: 8px;">
                    <strong>Address Location</strong><br>
                    ${address}<br>
                    Latitude: ${position[1].toFixed(6)}<br>
                    Longitude: ${position[0].toFixed(6)}
                </div>`,
                pixelOffset: [0, -30]
            })
        });

        map.markers.add(markers.search);
    },

    removeSearchMarker: function (map) {
        if (markers.search) {
            map.markers.remove(markers.search);
            markers.search = null;
        }
    },

    clearAllMarkers: function (map) {
        this.removeSearchMarker(map);
        // Add other marker removal methods here as needed
    },

    selectAddress: async function (map, position, address) {
        const [lon, lat] = position;
        const center = [lon, lat];
        const maxZoom = 18;

        this.addSearchMarker(map, center, address.trim());

        await navigateAndClick(map, center, maxZoom);
    },
    fuzzyAddressSearch: async function (input) {
        if (!input || !input.trim()) {
            return; // Don't search if input is empty
        }
        const token = await getToken();

        // Use token authentication - Azure Maps REST APIs require x-ms-client-id header when using token auth
        const url = `https://atlas.microsoft.com/search/address/json` +
            `?api-version=1.0` +
            `&countrySet=US` +
            `&limit=10` +
            `&lat=34.2257` +
            `&lon=-77.9447` +
            `&radius=50000` +
            '&typeahead=true' +        // 👈 treat as autocomplete
            '&idxSet=Addr' +           // 👈 prioritize addresses
            '&minFuzzyLevel=1' +       // 👈 MS best practice
            '&maxFuzzyLevel=2' +      // 👈 keep it sane
            `&query=${encodeURIComponent(input)}`;

        const res = await fetch(url, {
            headers: {
                'Authorization': `Bearer ${token}`,
                'x-ms-client-id': '6e0db76b-5702-4798-9c9a-405de3e52e5e' // Azure Maps client ID
            }
        });

        if (!res.ok) {
            const errorText = await res.text();
            console.error('Search API error:', res.status, errorText);
            throw new Error('Search failed: ' + res.status + ' - ' + errorText);
        }
        const json = await res.json();

        // Format and return address results
        const formattedResults = [];
        if (json.results && Array.isArray(json.results)) {
            json.results.forEach((result, index) => {
                if (result.address && result.type == "Point Address") {
                    const addr = result.address;
                    const addressParts = [];

                    // Street number and street name (no comma between them)
                    const streetParts = [];
                    if (addr.streetNumber) {
                        streetParts.push(addr.streetNumber);
                    }
                    if (addr.streetName) {
                        streetParts.push(addr.streetName);
                    }
                    if (streetParts.length > 0) {
                        addressParts.push(streetParts.join(' '));
                    }

                    // City and state (comma between these)
                    if (addr.municipality) {
                        addressParts.push(addr.municipality);
                    }
                    if (addr.countrySubdivisionName) {
                        addressParts.push(addr.countrySubdivisionName);
                    }

                    // Join street, city, and state with commas
                    let fullAddress = addressParts.join(', ');

                    // Add postal code with a space (no comma)
                    if (addr.postalCode) {
                        fullAddress += ' ' + addr.postalCode;
                    }

                    formattedResults.push({
                        address: fullAddress,
                        position: result.position ? [result.position.lon, result.position.lat] : null,
                        originalResult: result
                    });
                }
            });
        }

        return formattedResults;
    }
};

async function geocode(address) {
    // Get token (uses cache if available)
    const token = await getToken();
    const bbox = [-78.25, 33.70, -77.65, 34.35]; // minLon,minLat,maxLon,maxLat

    function inBbox(pos, [minLon, minLat, maxLon, maxLat]) {
        return (
            pos.lon >= minLon &&
            pos.lon <= maxLon &&
            pos.lat >= minLat &&
            pos.lat <= maxLat
        );
    }
    // Use token authentication - Azure Maps REST APIs require x-ms-client-id header when using token auth
    const url = `https://atlas.microsoft.com/search/address/json?api-version=1.0&countrySet=US&query=${encodeURIComponent(address)}`;
    const res = await fetch(url, {
        headers: {
            'Authorization': `Bearer ${token}`,
            'x-ms-client-id': '6e0db76b-5702-4798-9c9a-405de3e52e5e' // Azure Maps client ID
        }
    });

    if (!res.ok) {
        const errorText = await res.text();
        console.error('Search API error:', res.status, errorText);
        throw new Error('Search failed: ' + res.status + ' - ' + errorText);
    }
    const json = await res.json();
    const filteredResults = (json.results ?? []).filter(r =>
        r.position && inBbox(r.position, bbox)
    );
    const best = filteredResults[0];
    if (!best) {
        throw new Error('No geocode match.');
    }
    const { lat, lon } = best.position;
    return [lon, lat]; // [lng, lat]
}

// Reverse geocode - get address from coordinates
export async function reverseGeocode(lng, lat) {
    // Get token (uses cache if available)
    const token = await getToken();

    // Use reverse geocoding API
    const url = `https://atlas.microsoft.com/search/address/reverse/json?api-version=1.0&query=${lat},${lng}`;
    const res = await fetch(url, {
        headers: {
            'Authorization': `Bearer ${token}`,
            'x-ms-client-id': '6e0db76b-5702-4798-9c9a-405de3e52e5e' // Azure Maps client ID
        }
    });

    if (!res.ok) {
        const errorText = await res.text();
        console.error('Reverse geocode API error:', res.status, errorText);
        return null;
    }

    const json = await res.json();
    const result = json.addresses?.[0];

    if (!result || !result.address) {
        return null;
    }

    // Format the address similar to how we format in fuzzyAddressSearch
    const addr = result.address;
    const addressParts = [];

    // Street number and street name (no comma between them)
    const streetParts = [];
    if (addr.streetNumber) {
        streetParts.push(addr.streetNumber);
    }
    if (addr.streetName) {
        streetParts.push(addr.streetName);
    }
    if (streetParts.length > 0) {
        addressParts.push(streetParts.join(' '));
    }

    // City and state (comma between these)
    if (addr.municipality) {
        addressParts.push(addr.municipality);
    }
    if (addr.countrySubdivisionName) {
        addressParts.push(addr.countrySubdivisionName);
    }

    // Join street, city, and state with commas
    let fullAddress = addressParts.join(', ');

    // Add postal code with a space (no comma)
    if (addr.postalCode) {
        fullAddress += ' ' + addr.postalCode;
    }

    return fullAddress;
}

// Parcel search function
async function searchParcel(address) {
    if (!address || !address.trim()) {
        alert('Please enter an address');
        return;
    }

    try {
        // Geocode the address
        const lngLat = await geocode(address.trim());
        const [lon, lat] = lngLat;
        const center = [lon, lat];
        const maxZoom = 18; // Maximum zoom level

        const map = window.map;

        MapMarkers.addSearchMarker(map, center, address.trim());

        await navigateAndClick(map, center, maxZoom);

    } catch (error) {
        console.error('Parcel search error:', error);
        // Re-throw the error so it can be handled by the caller (e.g., Vue app with global modal)
        throw error;
    }
}

// Export for ES6 modules
export default MapMarkers;
export { geocode, searchParcel };

// Make it globally accessible for backward compatibility
window.MapMarkers = MapMarkers;
window.geocode = geocode;
window.reverseGeocode = reverseGeocode;
window.searchParcel = searchParcel;
